import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { ArrowRight, Pencil, Plus, Trash2 } from 'lucide-react';
import api from '../../../lib/api';
import Button from '../../../components/Button';
import QuillEditor from '../../../components/QuillEditor';
import RichContent, { normalizeRichText, richTextIsEmpty } from '../../../components/RichContent';
import Chip from '../../../components/Chip';
import ConfirmationDialog from '../../../components/ConfirmationDialog';
import AdminModal from '../../components/AdminModal';
import { formatDate } from '../../lib/format';
import { ExternalTextLink } from '../../../components/ballot-initiatives/detail/detailAtoms';
import { apiError, inputClasses, invalidateInitiativeQueries, labelClasses } from './InitiativeDetailPage';

export interface BlueBookSection {
  id: string;
  section: string;
  body: string;
  page_reference: string | null;
  display_order: number;
}

interface Props {
  initiativeId: string;
  sections: BlueBookSection[];
  edition: string | null;
  retrievedOn: string | null;
  entryStatus: string;
  verifiedAt: string | null;
  /** The booklet this measure links to: its own Blue Book URL, else its election cycle's. */
  bookUrl?: string | null;
}

const SECTION_LABELS: Record<string, string> = {
  summary: 'Summary',
  analysis: 'Analysis',
  vote_yes_means: 'What a YES vote means',
  vote_no_means: 'What a NO vote means',
  arguments_for: 'Arguments For',
  arguments_against: 'Arguments Against',
  fiscal_estimate: 'Fiscal estimate',
  background: 'Background',
  other: 'Other',
};

/** Sections with a matching measure column, for one-click transcription. */
const COPY_TARGETS: Record<string, { field: string; label: string }> = {
  summary: { field: 'plain_language_summary', label: 'Plain-Language Summary' },
  vote_yes_means: { field: 'what_yes_means', label: 'What a YES vote means' },
  vote_no_means: { field: 'what_no_means', label: 'What a NO vote means' },
  // The Blue Book's argument blocks seed the citizen "and why" fields as an
  // official-source starting point (PRD v2 §5.12.4); the section values arrive
  // with the Arguments For / Against section types.
  arguments_for: { field: 'who_supports_and_why', label: 'Who Supports It and Why' },
  arguments_against: { field: 'who_opposes_and_why', label: 'Who Opposes It and Why' },
};

const ENTRY_STATUSES = [
  { value: 'pending', label: 'Pending' },
  { value: 'entered', label: 'Entered' },
  { value: 'verified', label: 'Verified' },
  { value: 'not_applicable', label: 'Not applicable' },
];

const STATUS_VARIANTS: Record<string, 'success' | 'warning' | 'neutral' | 'info'> = {
  verified: 'success',
  entered: 'info',
  pending: 'warning',
  not_applicable: 'neutral',
};

const EMPTY_SECTION = { section: '', body: '', page_reference: '' };

/**
 * The Blue Book as a stored source of record.
 *
 * This card holds the *document* — what Legislative Council published, section by
 * section. The measure's own citizen fields hold *what we publish*. They are often
 * the same words, but they are not the same thing: a later Blue Book revision must be
 * able to change one without silently rewriting the other.
 *
 * Pro/con arguments are not here. They already live on the Sides tab with their side,
 * ordering and attribution — two homes for one thing is how they drift apart.
 */
const InitiativeBlueBookCard: React.FC<Props> = ({
  initiativeId, sections, edition, retrievedOn, entryStatus, verifiedAt, bookUrl,
}) => {
  const queryClient = useQueryClient();
  const [sectionModal, setSectionModal] = useState(false);
  const [editing, setEditing] = useState<BlueBookSection | null>(null);
  const [form, setForm] = useState(EMPTY_SECTION);
  const [metaModal, setMetaModal] = useState(false);
  const [meta, setMeta] = useState({ edition: '', retrieved: '', status: entryStatus });
  const [confirmDelete, setConfirmDelete] = useState<BlueBookSection | null>(null);
  const [copyTarget, setCopyTarget] = useState<BlueBookSection | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = () => invalidateInitiativeQueries(queryClient, initiativeId);

  const openAdd = () => {
    setEditing(null);
    setForm(EMPTY_SECTION);
    setError(null);
    setSectionModal(true);
  };

  const openEdit = (section: BlueBookSection) => {
    setEditing(section);
    setForm({
      section: section.section,
      body: section.body,
      page_reference: section.page_reference ?? '',
    });
    setError(null);
    setSectionModal(true);
  };

  const saveSection = useMutation({
    mutationFn: () => {
      const body = {
        section: form.section,
        body: normalizeRichText(form.body),
        page_reference: form.page_reference.trim() || null,
      };
      return editing
        ? api.put(`/admin/ballot-initiatives/${initiativeId}/blue-book/${editing.id}`, body)
        : api.post(`/admin/ballot-initiatives/${initiativeId}/blue-book`, body);
    },
    onSuccess: () => {
      toast.success(editing ? 'Section updated' : 'Section added');
      setSectionModal(false);
      refresh();
    },
    onError: (err: any) => setError(apiError(err, 'Failed to save the section')),
  });

  const deleteSection = useMutation({
    mutationFn: (section: BlueBookSection) =>
      api.delete(`/admin/ballot-initiatives/${initiativeId}/blue-book/${section.id}`),
    onSuccess: () => {
      toast.success('Section removed');
      setConfirmDelete(null);
      refresh();
    },
    onError: (err: any) => toast.error(apiError(err, 'Failed to remove the section')),
  });

  const saveMeta = useMutation({
    mutationFn: () => api.put(`/admin/ballot-initiatives/${initiativeId}`, {
      blue_book_edition: meta.edition.trim() || null,
      blue_book_retrieved_on: meta.retrieved || null,
      blue_book_entry_status: meta.status,
    }),
    onSuccess: () => {
      toast.success('Blue Book details saved');
      setMetaModal(false);
      refresh();
    },
    onError: (err: any) => toast.error(apiError(err, 'Failed to save Blue Book details')),
  });

  /**
   * Transcribe a section into its matching measure field, declaring it verbatim from
   * this edition. The confirmation is the human step: nothing is copied without it,
   * and the origin is set correctly rather than left for someone to remember.
   */
  const copyToMeasure = useMutation({
    mutationFn: (section: BlueBookSection) => {
      const target = COPY_TARGETS[section.section];
      return api.put(`/admin/ballot-initiatives/${initiativeId}`, {
        [target.field]: section.body,
        content_provenance: {
          [target.field]: {
            origin: 'official_source',
            source: [edition, section.page_reference].filter(Boolean).join(', ') || 'Blue Book',
          },
        },
      });
    },
    onSuccess: () => {
      toast.success('Copied to the measure, marked verbatim');
      setCopyTarget(null);
      refresh();
    },
    onError: (err: any) => toast.error(apiError(err, 'Failed to copy the section')),
  });

  return (
    <div className="card p-5">
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <h3 className="text-base font-semibold text-text-primary dark:text-text-dark-primary">Blue Book</h3>
          <Chip label={ENTRY_STATUSES.find(s => s.value === entryStatus)?.label ?? entryStatus}
            variant={STATUS_VARIANTS[entryStatus] ?? 'neutral'} size="sm" />
        </div>
        <div className="flex gap-1">
          <Button
            variant="ghost"
            size="sm"
            icon={Pencil}
            ariaLabel="Edit Blue Book details"
            onClick={() => {
              setMeta({
                edition: edition ?? '',
                retrieved: retrievedOn ? retrievedOn.slice(0, 10) : '',
                status: entryStatus,
              });
              setMetaModal(true);
            }}
          />
          <Button variant="ghost" size="sm" icon={Plus} onClick={openAdd}>Add Section</Button>
        </div>
      </div>

      <p className="mb-4 text-xs text-text-tertiary dark:text-text-dark-tertiary">
        {edition || 'No edition recorded'}
        {retrievedOn && ` · retrieved ${formatDate(retrievedOn)}`}
        {entryStatus === 'verified' && verifiedAt && ` · verified ${formatDate(verifiedAt)}`}
        {bookUrl && (
          <>
            {' · '}
            <ExternalTextLink href={bookUrl} className="text-xs">Read the booklet</ExternalTextLink>
          </>
        )}
      </p>

      {sections.length === 0 ? (
        <p className="text-sm text-text-tertiary dark:text-text-dark-tertiary">
          No Blue Book content entered. Pro/con arguments live on the Sides tab.
        </p>
      ) : (
        <ul className="space-y-3">
          {sections.map((section) => (
            <li key={section.id} className="border-l-2 border-border-light pl-3 dark:border-border-dark">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-text-primary dark:text-text-dark-primary">
                    {SECTION_LABELS[section.section] ?? section.section}
                    {section.page_reference && (
                      <span className="ml-1.5 text-xs font-normal text-text-tertiary dark:text-text-dark-tertiary">
                        p. {section.page_reference}
                      </span>
                    )}
                  </p>
                  <RichContent
                    html={section.body}
                    className="mt-0.5 text-sm text-text-secondary dark:text-text-dark-secondary"
                  />
                </div>
                <div className="flex shrink-0 gap-1">
                  {COPY_TARGETS[section.section] && (
                    <Button
                      variant="ghost"
                      size="sm"
                      icon={ArrowRight}
                      ariaLabel="Copy to measure field"
                      onClick={() => setCopyTarget(section)}
                    />
                  )}
                  <Button variant="ghost" size="sm" icon={Pencil} ariaLabel="Edit section" onClick={() => openEdit(section)} />
                  <Button variant="ghost" size="sm" icon={Trash2} ariaLabel="Remove section" onClick={() => setConfirmDelete(section)} />
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      <AdminModal
        isOpen={sectionModal}
        onClose={() => setSectionModal(false)}
        title={editing ? 'Edit Blue Book Section' : 'Add Blue Book Section'}
        size="lg"
        footer={<>
          <Button variant="secondary" size="sm" onClick={() => setSectionModal(false)}>Cancel</Button>
          <Button
            variant="primary"
            size="sm"
            onClick={() => saveSection.mutate()}
            disabled={!form.section || richTextIsEmpty(form.body) || saveSection.isPending}
          >
            {saveSection.isPending ? 'Saving…' : 'Save'}
          </Button>
        </>}
      >
        <div className="space-y-4">
          {error && (
            <p className="rounded-lg bg-danger-light px-3 py-2 text-sm text-danger dark:bg-danger-dark/20">{error}</p>
          )}
          <div>
            <label htmlFor="bb-section" className={labelClasses}>Section *</label>
            <select
              id="bb-section"
              value={form.section}
              onChange={(e) => setForm((c) => ({ ...c, section: e.target.value }))}
              className={inputClasses}
            >
              <option value="">Select a section…</option>
              {Object.entries(SECTION_LABELS).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>
          <div>
            <span className={labelClasses}>Text as published *</span>
            <QuillEditor
              value={form.body}
              onChange={(value) => setForm((c) => ({ ...c, body: value }))}
              placeholder="Paste the section verbatim"
            />
          </div>
          <div>
            <label htmlFor="bb-page" className={labelClasses}>Page reference</label>
            <input
              id="bb-page"
              type="text"
              value={form.page_reference}
              onChange={(e) => setForm((c) => ({ ...c, page_reference: e.target.value }))}
              className={inputClasses}
              placeholder="e.g. 12"
            />
          </div>
        </div>
      </AdminModal>

      <AdminModal
        isOpen={metaModal}
        onClose={() => setMetaModal(false)}
        title="Blue Book Details"
        footer={<>
          <Button variant="secondary" size="sm" onClick={() => setMetaModal(false)}>Cancel</Button>
          <Button variant="primary" size="sm" onClick={() => saveMeta.mutate()} disabled={saveMeta.isPending}>
            {saveMeta.isPending ? 'Saving…' : 'Save'}
          </Button>
        </>}
      >
        <div className="space-y-4">
          <div>
            <label htmlFor="bb-edition" className={labelClasses}>Edition</label>
            <input
              id="bb-edition"
              type="text"
              value={meta.edition}
              onChange={(e) => setMeta((c) => ({ ...c, edition: e.target.value }))}
              className={inputClasses}
              placeholder="e.g. 2026 Blue Book (revised Sept 18)"
            />
          </div>
          <div>
            <label htmlFor="bb-retrieved" className={labelClasses}>Retrieved on</label>
            <input
              id="bb-retrieved"
              type="date"
              value={meta.retrieved}
              onChange={(e) => setMeta((c) => ({ ...c, retrieved: e.target.value }))}
              className={inputClasses}
            />
          </div>
          <div>
            <label htmlFor="bb-status" className={labelClasses}>Entry status</label>
            <select
              id="bb-status"
              value={meta.status}
              onChange={(e) => setMeta((c) => ({ ...c, status: e.target.value }))}
              className={inputClasses}
            >
              {ENTRY_STATUSES.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
            <p className="mt-1 text-xs text-text-tertiary dark:text-text-dark-tertiary">
              Editing any section afterwards returns a verified measure to Entered — a
              verification stands for what was checked, so changing it retracts the check.
            </p>
          </div>
        </div>
      </AdminModal>

      <ConfirmationDialog
        isOpen={confirmDelete !== null}
        onCancel={() => setConfirmDelete(null)}
        onConfirm={() => confirmDelete && deleteSection.mutate(confirmDelete)}
        title="Remove this section?"
        message={confirmDelete ? `The ${SECTION_LABELS[confirmDelete.section] ?? confirmDelete.section} section will be removed.` : ''}
        confirmText="Remove"
        variant="danger"
      />

      <ConfirmationDialog
        isOpen={copyTarget !== null}
        onCancel={() => setCopyTarget(null)}
        onConfirm={() => copyTarget && copyToMeasure.mutate(copyTarget)}
        title="Copy to the measure?"
        message={
          copyTarget
            ? `This replaces "${COPY_TARGETS[copyTarget.section]?.label}" on the measure with the Blue Book text and marks it verbatim from ${edition || 'the Blue Book'}.`
            : ''
        }
        confirmText="Copy"
        variant="warning"
      />
    </div>
  );
};

export default InitiativeBlueBookCard;
